import { type JSX, useEffect, useRef, useState } from "react";
import tarskiLockupLight from "../../../design/tarski-kit/assets/tarski-logo-blue.svg";
import tarskiLockupDark from "../../../design/tarski-kit/assets/tarski-logo-dark-lockup.svg";
import {
  readWebStudioSignInAvailability,
  type WebStudioSignInAvailabilityView,
} from "../../api/auth";
import type { WebStudioBootSnapshot, WebStudioScope } from "../../api/boot";
import type { WebStudioDiagnostic } from "../../api/generatedClient";
import { Card, useAppearance } from "../../design";
import { SignInCodeFlow } from "./SignInCodeFlow";
import { clearSignInCodeStep } from "./signInFlowMemory";
import "./authGate.css";

/** Minimum spacing between focus-driven boot re-runs; keeps the check cheap. */
const FOCUS_REBOOT_MIN_INTERVAL_MS = 1_000;

export interface SignInGateProps {
  scope: WebStudioScope;
  /** The signed-out boot snapshot that classified this session, when read. */
  boot: WebStudioBootSnapshot | null;
  /** Machine-context diagnostic code fallback while no boot is readable. */
  diagnosticCode: string | null;
  /** Re-runs boot; the shell renders only after boot reports authorized. */
  onBootRefresh: () => void;
}

/**
 * The only surface rendered while the backend reports no authorized account
 * session. Content is backend-owned: the typed signed-out diagnostic and the
 * boot payload's declared sign-in availability. No shell chrome, no
 * destination content, and no confirmation or denial of any deep-linked
 * resource.
 */
export function SignInGate({
  scope,
  boot,
  diagnosticCode,
  onBootRefresh,
}: SignInGateProps): JSX.Element {
  const appearance = useAppearance();
  const lastFocusReboot = useRef(0);
  // Flow continuity is only meaningful within one gated session: when the
  // gate unmounts (authorized, blocked, or failed), a remembered code step
  // must never resurface in a later gate.
  useEffect(() => () => clearSignInCodeStep(), []);
  // Each signed-out boot snapshot delivered while gated bumps this counter so
  // the code flow can detect a confirmation that never became a session.
  const [signedOutBootCount, setSignedOutBootCount] = useState(0);
  const lastCountedBoot = useRef<WebStudioBootSnapshot | null>(null);
  useEffect(() => {
    if (boot !== null && boot !== lastCountedBoot.current) {
      lastCountedBoot.current = boot;
      setSignedOutBootCount((count) => count + 1);
    }
  }, [boot]);

  // Cross-tab reconciliation: a parked gate tab re-runs boot when it regains
  // focus or visibility so a sign-in completed in another tab unlocks it.
  useEffect(() => {
    const rebootIfDue = (): void => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastFocusReboot.current < FOCUS_REBOOT_MIN_INTERVAL_MS) {
        return;
      }
      lastFocusReboot.current = now;
      onBootRefresh();
    };
    window.addEventListener("focus", rebootIfDue);
    document.addEventListener("visibilitychange", rebootIfDue);
    return () => {
      window.removeEventListener("focus", rebootIfDue);
      document.removeEventListener("visibilitychange", rebootIfDue);
    };
  }, [onBootRefresh]);

  // Boot refetches while gated briefly read as null; keep the last-known
  // backend-declared availability so the in-progress sign-in flow stays
  // mounted instead of collapsing to the confirming state.
  const lastAvailability = useRef<WebStudioSignInAvailabilityView | null>(null);
  const lastDiagnostic = useRef<WebStudioDiagnostic | null>(null);
  if (boot !== null) {
    lastAvailability.current = readWebStudioSignInAvailability(boot);
    lastDiagnostic.current = boot.diagnostics[0]?.diagnostic ?? null;
  }
  const diagnostic: WebStudioDiagnostic | null = lastDiagnostic.current;
  const availability: WebStudioSignInAvailabilityView | null = lastAvailability.current;

  return (
    <main className="auth-gate" aria-label="Sign in to JacqOS Studio">
      <div className="auth-gate__panel">
        <img
          src={appearance.theme === "dark" ? tarskiLockupDark : tarskiLockupLight}
          alt="tarski"
          className="auth-gate__brand"
        />
        <h1 className="auth-gate__title">Sign in to Studio</h1>
        {diagnostic !== null ? (
          <p className="auth-gate__diagnostic" role="status">
            <span className="mono">{diagnostic.code}</span> {diagnostic.message}
          </p>
        ) : diagnosticCode !== null ? (
          <p className="auth-gate__diagnostic" role="status">
            <span className="mono">{diagnosticCode}</span>
          </p>
        ) : null}
        <Card label="authentication" className="auth-gate__card">
          {availability === null ? (
            <p className="muted" role="status">
              Confirming session state with the backend…
            </p>
          ) : availability.availability === "available" ? (
            <SignInCodeFlow
              scope={scope}
              availability={availability}
              onSignedIn={onBootRefresh}
              signedOutBootCount={signedOutBootCount}
            />
          ) : (
            <SignInUnavailable availability={availability} />
          )}
        </Card>
      </div>
    </main>
  );
}

/**
 * Sign-in is unavailable rather than silently broken: the gate renders the
 * backend's typed availability diagnostic and guidance, and never fabricates
 * a sign-in affordance the backend did not declare.
 */
function SignInUnavailable({
  availability,
}: {
  availability: WebStudioSignInAvailabilityView;
}): JSX.Element {
  return (
    <div className="auth-gate__unavailable" role="status">
      {availability.diagnosticCode === null ? null : (
        <p className="auth-gate__diagnostic">
          <span className="mono">{availability.diagnosticCode}</span>
        </p>
      )}
      {availability.guidance === null ? (
        <p className="muted">
          {availability.availability === "operations_absent"
            ? "This backend does not publish in-Studio sign-in operations. Upgrade the management plane to a version with the sign-in contract, or sign in through a supported client."
            : "Interactive sign-in is not available from this backend."}
        </p>
      ) : (
        <p className="muted">{availability.guidance}</p>
      )}
    </div>
  );
}
